import { useEffect, useRef, useState } from "react";
import { CCol, CRow } from "@coreui/react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { ReactSortable } from "react-sortablejs";
import { useCookie } from "../../hooks/useCookie";
import { useToast } from "../../hooks/useToast";
import apiService from "../../service/apiService";
import AdminRoute from "../auth/RestrictedRoute";
import SearchForm from "../../components/inputs/SearchForm";
import { useDebounce } from "../../hooks/useDebounce";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { colors } from "../../config/theme";

const activityPlaceholderImage = "/venez.jpeg";

interface ActivityItem {
  id: string;
  title: string;
  order: number;
  status: string;
  galleries?: { url: string }[];
  place?: { name: string };
  translations?: { title: string; lang: string }[];
}

export default function OrganizeActivities() {
  const { token } = useCookie("vToken");
  const queryClient = useQueryClient();
  const { toastError, toastSuccess } = useToast();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [orderInputValue, setOrderInputValue] = useState<number>(0);
  const initialOrderRef = useRef<string[]>([]);

  const { data, isLoading } = useQuery(
    ["getActivitiesOrganize", debouncedSearch],
    async () => {
      const params = new URLSearchParams({
        page: "1",
        limit: "100",
        search: debouncedSearch,
      });
      const res = await apiService.MakeGetRequest(
        `activities?${params.toString()}`,
        token,
      );
      return res?.items ?? [];
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity, // don't let background refetches overwrite local order
    },
  );

  const [arrangedActivities, setArrangedActivities] = useState<ActivityItem[]>(
    [],
  );

  useEffect(() => {
    if (data) {
      setArrangedActivities(data.map((item: ActivityItem) => ({ ...item }))); // copy
      initialOrderRef.current = data.map((item: ActivityItem) => item.id);
    }
  }, [data]);

  // Save order mutation — sends individual PUT requests sequentially to avoid PostgreSQL transaction deadlocks
  const saveOrderMutation = useMutation(
    async (activities: ActivityItem[]) => {
      const currentOrder = [...initialOrderRef.current];

      for (let i = 0; i < activities.length; i++) {
        const targetId = activities[i].id;
        const currentIdx = currentOrder.indexOf(targetId);

        // If item is already at the desired position in the database order, skip!
        if (currentIdx === i) continue;

        // Move targetId to position (i + 1)
        await apiService.MakePutRequest(
          `activities/${targetId}/order`,
          { order: i + 1 },
          token,
        );

        // Mirror backend's splice & insert in our local tracking array
        if (currentIdx !== -1) {
          const [moved] = currentOrder.splice(currentIdx, 1);
          currentOrder.splice(i, 0, moved);
        } else {
          currentOrder.splice(i, 0, targetId);
        }
      }

      initialOrderRef.current = activities.map((a) => a.id);
    },
    {
      onError: (err: any) => {
        console.error("Failed to update activity order", err);
        toastError(
          err?.response?.data?.message || "Quelque chose n'a pas fonctionné",
        );
      },
      onSuccess: (_res, activities) => {
        toastSuccess("Ordre des activités mis à jour");
        queryClient.setQueryData(
          ["getActivitiesOrganize", debouncedSearch],
          activities.map((a, i) => ({ ...a, order: i + 1 })),
        );
        queryClient.invalidateQueries(["getActivities"]);
      },
    },
  );

  const handleOrderChange = (activityId: string, newOrder: number) => {
    if (newOrder < 1 || newOrder > arrangedActivities.length) return;

    const updated = [...arrangedActivities];
    const idx = updated.findIndex((a) => a.id === activityId);
    if (idx === -1) return;

    const [item] = updated.splice(idx, 1);
    updated.splice(newOrder - 1, 0, item);

    setArrangedActivities(updated.map((a, i) => ({ ...a, order: i + 1 })));
    setEditingOrderId(null);
  };

  const savePositions = () => {
    saveOrderMutation.mutate(arrangedActivities);
  };

  const getDisplayTitle = (activity: ActivityItem) => {
    if (activity.title) return activity.title;
    const frTranslation = activity.translations?.find((t) => t.lang === "fr");
    return (
      frTranslation?.title || activity.translations?.[0]?.title || "Sans titre"
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return (
          <span className="badge bg-success" style={{ fontSize: "0.7rem" }}>
            Approuvé
          </span>
        );
      case "draft":
        return (
          <span className="badge bg-secondary" style={{ fontSize: "0.7rem" }}>
            Brouillon
          </span>
        );
      case "pending_review":
        return (
          <span
            className="badge bg-warning text-dark"
            style={{ fontSize: "0.7rem" }}
          >
            En attente
          </span>
        );
      case "rejected":
        return (
          <span className="badge bg-danger" style={{ fontSize: "0.7rem" }}>
            Rejeté
          </span>
        );
      default:
        return (
          <span
            className="badge bg-light text-dark"
            style={{ fontSize: "0.7rem" }}
          >
            {status}
          </span>
        );
    }
  };

  return (
    <AdminRoute>
      <CRow className="bg-white p-3 rounded-md mb-3 mx-auto align-items-center">
        <CCol className="col-12 col-sm-7 col-md-8">
          <p className="organize__offer__description">
            Déplacer une activité ou entrer un numéro pour spécifier son
            emplacement dans l'application
          </p>
        </CCol>
        <CCol className="col-12 col-sm-5 col-md-4 mt-3 mt-sm-0">
          <div className="d-flex align-items-center justify-content-between gap-2">
            <SearchForm onChange={setSearchQuery} value={searchQuery} />
            <button
              onClick={savePositions}
              className="btn btn-danger text-white ms-auto d-block shadow-primary text-nowrap"
              disabled={
                !arrangedActivities?.length || saveOrderMutation.isLoading
              }
            >
              {saveOrderMutation.isLoading ? "Mise à jour..." : "Mettre à jour"}
            </button>
          </div>
        </CCol>
      </CRow>

      {isLoading ? (
        <div className="d-flex justify-content-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      ) : arrangedActivities.length === 0 ? (
        <div className="d-flex flex-column align-items-center justify-content-center my-5 py-5 bg-white rounded-md">
          <MagnifyingGlassIcon width={48} height={48} color="gray" />
          <p className="text-muted mt-3 mb-1 fs-5 fw-semibold">
            {debouncedSearch
              ? "Aucune activité trouvée"
              : "Aucune activité disponible"}
          </p>
          <p className="text-muted small mb-0">
            {debouncedSearch
              ? `Aucun résultat pour "${debouncedSearch}". Essayez un autre terme.`
              : "Commencez par créer des activités pour les organiser ici."}
          </p>
        </div>
      ) : (
        <ReactSortable
          list={arrangedActivities}
          setList={(newList) =>
            setArrangedActivities(
              newList.map(({ chosen, selected, ...rest }: any) => rest),
            )
          }
          className="organize__offers_wrapper"
          animation={150}
          disabled={saveOrderMutation.isLoading}
        >
          {arrangedActivities.map((activity, index) => (
            <div
              key={activity.id}
              className="organize-offer-item mb-3 p-3 bg-white rounded"
              style={{ cursor: "grab" }}
            >
              {/* Header row: place + status + order input */}
              <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-2 gap-2">
                {/* Drag hint icon */}
                <span
                  className="text-muted"
                  style={{
                    fontSize: "1.1rem",
                    userSelect: "none",
                    flexShrink: 0,
                  }}
                >
                  ⠿
                </span>

                {/* Place name */}
                <span
                  className="text-muted small flex-grow-1"
                  style={{
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {activity.place?.name || "—"}
                </span>

                {/* Status badge */}
                {getStatusBadge(activity.status)}

                {/* Order input / display */}
                {editingOrderId === activity.id ? (
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    min="1"
                    max={arrangedActivities.length}
                    value={orderInputValue || ""}
                    onChange={(e) =>
                      setOrderInputValue(
                        e.target.value === "" ? 0 : parseInt(e.target.value),
                      )
                    }
                    style={{ width: "60px" }}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleOrderChange(
                          activity.id,
                          Math.max(1, orderInputValue),
                        );
                      } else if (e.key === "Escape") {
                        setEditingOrderId(null);
                      }
                    }}
                    onBlur={() => {
                      handleOrderChange(
                        activity.id,
                        orderInputValue < 1 ? 1 : orderInputValue,
                      );
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <p
                    className="fw-bold p-1 border text-center my-0 cursor-pointer"
                    style={{
                      marginLeft: "auto",
                      width: "30px",
                      height: "30px",
                      color: colors.primary,
                      borderColor: colors.primary,
                      flexShrink: 0,
                    }}
                    onClick={() => {
                      setOrderInputValue(index + 1);
                      setEditingOrderId(activity.id);
                    }}
                    title="Cliquer pour modifier l'ordre"
                  >
                    {index + 1}
                  </p>
                )}
              </div>

              {/* Body: thumbnail + title */}
              <div className="d-flex align-items-center gap-2">
                <img
                  src={activity.galleries?.[0]?.url || activityPlaceholderImage}
                  alt={getDisplayTitle(activity)}
                  width="45px"
                  height="45px"
                  style={{
                    objectFit: "cover",
                    borderRadius: "6px",
                    flexShrink: 0,
                  }}
                />
                <p
                  className="my-0 fw-semibold"
                  style={{
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {getDisplayTitle(activity)}
                </p>
              </div>
            </div>
          ))}
        </ReactSortable>
      )}
    </AdminRoute>
  );
}
